import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

function extractErrorMessage(err) {
  if (err.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => (e?.msg || JSON.stringify(e))).filter(Boolean).join('. ');
    }
    return String(detail);
  }
  if (err.response?.data?.message) return err.response.data.message;
  if (err.response?.status) {
    if (err.response.status === 401) return 'Invalid email or password.';
    if (err.response.status >= 500) return 'Server error. Please try again in a moment.';
    return `Request failed (${err.response.status}).`;
  }
  if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
    return 'Unable to connect to the server. Please try again in a moment.';
  }
  return err.message || 'Something went wrong. Please try again.';
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const result = await login(email, password);
      toast.success('Welcome back!');
      
      if (!result.user.onboarding_complete) {
        navigate('/onboarding');
      } else {
        navigate('/discover');
      }
    } catch (err) {
      const message = extractErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-in">
          <Link to="/" className="flex items-center gap-3 mb-16 group" data-testid="login-logo">
            <Heart className="w-9 h-9 text-[#6E1A24] transition-transform group-hover:scale-110" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
            <span className="text-2xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
          </Link>
          
          <h1 className="text-4xl font-serif font-light text-[#2D2D2A] mb-3">{t('auth.login_title', 'Welcome back')}</h1>
          <p className="text-lg text-[#6C6C67] font-light mb-10">
            {t('auth.login_subtitle', 'Continue your journey to find your Portuguese connection.')}
          </p>
          
          {error && (
            <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('auth.email', 'Email')}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6C67]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white border border-[#E5E3DB] rounded-2xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all text-base"
                  placeholder="your@email.com"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('auth.password', 'Password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6C67]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-4 bg-white border border-[#E5E3DB] rounded-2xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all text-base"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6C6C67] hover:text-[#2D2D2A] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-[#6E1A24] hover:underline font-medium" data-testid="forgot-password-link">
                {t('auth.forgot_password', 'Forgot password?')}
              </Link>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#6E1A24] text-white rounded-2xl py-4 font-medium text-base hover:bg-[#8A2331] hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('auth.signing_in', 'Signing in...')}
                </>
              ) : (
                t('auth.login_btn', 'Sign In')
              )}
            </Button>
          </form>
          
          <div className="my-10 flex items-center gap-4">
            <div className="flex-1 h-px bg-[#E5E3DB]" />
            <span className="text-sm text-[#6C6C67]">{t('common.or', 'or')}</span>
            <div className="flex-1 h-px bg-[#E5E3DB]" />
          </div>
          
          <Button 
            type="button"
            onClick={googleLogin}
            className="w-full bg-white border-2 border-[#E5E3DB] text-[#2D2D2A] rounded-2xl py-4 font-medium hover:bg-[#F5F2EB] hover:border-[#6E1A24]/20 transition-all flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.login_google', 'Continue with Google')}
          </Button>
          
          <p className="mt-10 text-center text-[#6C6C67]">
            {t('auth.login_no_account', "Don't have an account?")}{' '}
            <Link to="/register" className="text-[#6E1A24] font-medium hover:underline" data-testid="register-link">
              {t('auth.login_register_link', 'Create one')}
            </Link>
          </p>
        </div>
      </div>
      
      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6E1A24]/60 to-[#C97A63]/40" />
        <div className="absolute inset-0 flex flex-col justify-end p-16">
          <div className="max-w-md">
            <p className="text-3xl font-serif font-light text-white leading-relaxed mb-6">
              "I found someone who truly understands what it means to carry Portugal in your heart, even when you're far from home."
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm overflow-hidden">
                <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" alt="" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-white font-medium">Maria Santos</p>
                <p className="text-white/70 text-sm">Paris, France</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
